import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [gallery] - The horizontal media scroller.
 * @property {HTMLButtonElement} [previousButton] - The previous media button.
 * @property {HTMLButtonElement} [nextButton] - The next media button.
 * @property {HTMLElement} [status] - The current media status.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;
  #scrollTarget;
  #scale = 1;
  #pan = { x: 0, y: 0 };
  #drag = null;
  #touchGesture = null;
  #lastTap = null;
  #lastTouchTime = -Infinity;
  #resizeObserver = new ResizeObserver(() => {
    if (this.refs.dialog.open) {
      this.#resetZoom();
      this.selectThumbnail(this.#activeIndex, { behavior: 'instant' });
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#scrollTarget = this.refs.gallery || this.refs.dialog;
    this.#scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
    this.#scrollTarget.addEventListener('pointerdown', this.handleZoomPointerDown);
    this.#scrollTarget.addEventListener('pointermove', this.handleZoomPointerMove);
    this.#scrollTarget.addEventListener('pointerup', this.handleZoomPointerUp);
    this.#scrollTarget.addEventListener('pointercancel', this.handleZoomPointerUp);
    this.#scrollTarget.addEventListener('dragstart', this.handleZoomDragStart);
    this.#scrollTarget.addEventListener('touchstart', this.handleZoomTouchStart, { passive: false });
    this.#scrollTarget.addEventListener('touchmove', this.handleZoomTouchMove, { passive: false });
    this.#scrollTarget.addEventListener('touchend', this.handleZoomTouchEnd, { passive: false });
    this.#scrollTarget.addEventListener('touchcancel', this.handleZoomTouchCancel, { passive: false });
    this.#scrollTarget.addEventListener('dblclick', this.handleZoomDoubleClick);
    this.#scrollTarget.addEventListener('wheel', this.handleZoomWheel, { passive: false });
    this.#resizeObserver.observe(this.#scrollTarget);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.#scrollTarget?.removeEventListener('pointerdown', this.handleZoomPointerDown);
    this.#scrollTarget?.removeEventListener('pointermove', this.handleZoomPointerMove);
    this.#scrollTarget?.removeEventListener('pointerup', this.handleZoomPointerUp);
    this.#scrollTarget?.removeEventListener('pointercancel', this.handleZoomPointerUp);
    this.#scrollTarget?.removeEventListener('dragstart', this.handleZoomDragStart);
    this.#scrollTarget?.removeEventListener('touchstart', this.handleZoomTouchStart);
    this.#scrollTarget?.removeEventListener('touchmove', this.handleZoomTouchMove);
    this.#scrollTarget?.removeEventListener('touchend', this.handleZoomTouchEnd);
    this.#scrollTarget?.removeEventListener('touchcancel', this.handleZoomTouchCancel);
    this.#scrollTarget?.removeEventListener('dblclick', this.handleZoomDoubleClick);
    this.#scrollTarget?.removeEventListener('wheel', this.handleZoomWheel);
    this.#resetZoom();
    this.#resizeObserver.disconnect();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault();
    index = Number(index);

    const { dialog, media } = this.refs;
    const targetImage = media[index];
    if (!targetImage || dialog.open) return;

    const open = () => {
      dialog.showModal();
      this.selectThumbnail(index, { behavior: 'instant' });
      this.refs.status?.focus?.({ preventScroll: true });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    newImage.removeAttribute('srcset');
    newImage.removeAttribute('sizes');
    newImage.removeAttribute('ref');
    newImage.loading = 'eager';
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // Preserve the touched DOM node so a load finishing during a gesture
    // cannot detach the touch event target from this gallery.
    newImage.onload = () => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.loading = 'eager';
      image.src = highResolutionUrl;
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (!this.refs.dialog.open || !this.refs.media.length) return;
    const bounds = this.#scrollTarget.getBoundingClientRect();
    const center = bounds.left + bounds.width / 2;
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    this.refs.media.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < nearestDistance) {
        nearestIndex = index;
        nearestDistance = distance;
      }
    });
    this.#updateSelection(nearestIndex);
  }, 50);

  #updateSelection(index) {
    const { media, thumbnails, previousButton, nextButton, status } = this.refs;
    if (!media[index]) return;
    if (index !== this.#activeIndex) this.#resetZoom();
    this.#activeIndex = index;
    media.forEach((element, i) => element.classList.toggle('bs-fullscreen-gallery__active', i === index));
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
      if (i === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    if (previousButton) previousButton.disabled = index === 0;
    if (nextButton) nextButton.disabled = index === media.length - 1;
    if (status) status.textContent = status.dataset.statusLabel.replace('__INDEX__', String(index + 1));
    const targetThumbnail = thumbnails.children[index];
    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, { ancestor: thumbnails, behavior: 'instant', block: 'center', inline: 'center' });
    }
    this.loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  previous() {
    this.selectThumbnail(Math.max(0, this.#activeIndex - 1));
  }

  next() {
    this.selectThumbnail(Math.min(this.refs.media.length - 1, this.#activeIndex + 1));
  }

  #resetZoom() {
    this.refs.dialog.classList.remove('is-zoomed');
    this.#scale = 1;
    this.#pan = { x: 0, y: 0 };
    this.#releaseMouseCapture();
    this.#drag = null;
    this.#touchGesture = null;
    this.#lastTap = null;
    const slide = this.refs.media[this.#activeIndex];
    for (const name of ['--bs-zoom-scale', '--bs-zoom-x', '--bs-zoom-y']) slide?.style.removeProperty(name);
  }

  #isImageTarget(target) {
    const slide = this.refs.media[this.#activeIndex];
    return this.refs.dialog.open && slide?.classList.contains('product-media-container--image')
      && target instanceof Element && slide.contains(target)
      && Boolean(target.closest('.bs-fullscreen-gallery__image'));
  }

  #setZoom(scale, x = this.#pan.x, y = this.#pan.y) {
    const slide = this.refs.media[this.#activeIndex];
    if (!slide) return;
    this.#scale = Math.max(1, Math.min(3, scale));
    if (this.#scale <= 1.01) this.#scale = 1;
    const bounds = this.#scrollTarget.getBoundingClientRect();
    const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
    this.#pan = {
      x: clamp(x, bounds.width * (this.#scale - 1) / 2),
      y: clamp(y, bounds.height * (this.#scale - 1) / 2),
    };
    this.refs.dialog.classList.toggle('is-zoomed', this.#scale > 1);
    slide.style.setProperty('--bs-zoom-scale', String(this.#scale));
    slide.style.setProperty('--bs-zoom-x', `${this.#pan.x}px`);
    slide.style.setProperty('--bs-zoom-y', `${this.#pan.y}px`);
  }

  #zoomAt(scale, point) {
    const bounds = this.#scrollTarget.getBoundingClientRect();
    const x = point.clientX - bounds.left - bounds.width / 2;
    const y = point.clientY - bounds.top - bounds.height / 2;
    const nextScale = Math.max(1, Math.min(3, scale));
    const ratio = nextScale / this.#scale;
    this.#setZoom(nextScale, x - (x - this.#pan.x) * ratio, y - (y - this.#pan.y) * ratio);
  }

  #startPinch(touches) {
    const [first, second] = touches;
    const bounds = this.#scrollTarget.getBoundingClientRect();
    this.#lastTap = null;
    this.#touchGesture = {
      type: 'pinch',
      distance: Math.max(1, Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY)),
      scale: this.#scale,
      x: (first.clientX + second.clientX) / 2 - bounds.left - bounds.width / 2,
      y: (first.clientY + second.clientY) / 2 - bounds.top - bounds.height / 2,
      panX: this.#pan.x,
      panY: this.#pan.y,
    };
  }

  #startTouchPan(touch, timeStamp, allowTap = true) {
    this.#touchGesture = {
      type: this.#scale > 1 ? 'pan' : 'tap',
      startX: touch.clientX, startY: touch.clientY,
      panX: this.#pan.x, panY: this.#pan.y, timeStamp, allowTap,
    };
  }

  handleZoomTouchStart = (event) => {
    if (!this.#isImageTarget(event.target)) return;
    this.#lastTouchTime = event.timeStamp;
    if (event.touches.length >= 2) {
      if (event.cancelable) event.preventDefault();
      this.#startPinch(event.touches);
    } else if (event.touches.length === 1) {
      if (this.#scale > 1 && event.cancelable) event.preventDefault();
      this.#startTouchPan(event.touches[0], event.timeStamp);
    }
  };

  handleZoomTouchMove = (event) => {
    if (!this.#isImageTarget(event.target)) return;
    if (event.touches.length >= 2) {
      if (event.cancelable) event.preventDefault();
      if (this.#touchGesture?.type !== 'pinch') this.#startPinch(event.touches);
      const [first, second] = event.touches;
      const start = this.#touchGesture;
      const distance = Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
      const scale = Math.max(1, Math.min(3, start.scale * distance / start.distance));
      const bounds = this.#scrollTarget.getBoundingClientRect();
      const x = (first.clientX + second.clientX) / 2 - bounds.left - bounds.width / 2;
      const y = (first.clientY + second.clientY) / 2 - bounds.top - bounds.height / 2;
      this.#setZoom(scale, x - (start.x - start.panX) * scale / start.scale, y - (start.y - start.panY) * scale / start.scale);
    } else if (event.touches.length === 1 && this.#scale > 1) {
      if (event.cancelable) event.preventDefault();
      if (this.#touchGesture?.type !== 'pan') this.#startTouchPan(event.touches[0], event.timeStamp, false);
      const start = this.#touchGesture;
      this.#setZoom(this.#scale, start.panX + event.touches[0].clientX - start.startX, start.panY + event.touches[0].clientY - start.startY);
    } else if (this.#touchGesture?.type === 'tap' && event.touches[0]) {
      const touch = event.touches[0];
      if (Math.hypot(touch.clientX - this.#touchGesture.startX, touch.clientY - this.#touchGesture.startY) > 10) this.#touchGesture = null;
    }
  };

  handleZoomTouchEnd = (event) => {
    if (!this.#isImageTarget(event.target)) return;
    this.#lastTouchTime = event.timeStamp;
    if (event.touches.length >= 2) {
      this.#startPinch(event.touches);
      return;
    }
    if (event.touches.length === 1) {
      this.#lastTap = null;
      this.#startTouchPan(event.touches[0], event.timeStamp, false);
      return;
    }
    const start = this.#touchGesture;
    this.#touchGesture = null;
    const touch = event.changedTouches[0];
    if (!start || start.type === 'pinch' || start.allowTap === false || !touch || event.timeStamp - start.timeStamp > 250
      || Math.hypot(touch.clientX - start.startX, touch.clientY - start.startY) > 10) return;
    const previous = this.#lastTap;
    if (previous && event.timeStamp - previous.timeStamp < 300
      && Math.hypot(touch.clientX - previous.x, touch.clientY - previous.y) < 40) {
      if (event.cancelable) event.preventDefault();
      this.#lastTap = null;
      if (this.#scale > 1) this.#resetZoom();
      else this.#zoomAt(2, touch);
    } else {
      this.#lastTap = { timeStamp: event.timeStamp, x: touch.clientX, y: touch.clientY };
    }
  };

  handleZoomTouchCancel = () => {
    this.#touchGesture = null;
    this.#lastTap = null;
  };

  handleZoomDoubleClick = (event) => {
    if (!this.#isImageTarget(event.target)) return;
    if (event.sourceCapabilities?.firesTouchEvents || event.timeStamp - this.#lastTouchTime < 500) return;
    event.preventDefault();
    if (this.#scale > 1) this.#resetZoom();
    else this.#zoomAt(2, event);
  };

  handleZoomWheel = (event) => {
    if (!event.ctrlKey || !this.#isImageTarget(event.target)) return;
    event.preventDefault();
    this.#zoomAt(this.#scale * Math.exp(-event.deltaY * 0.008), event);
  };

  handleZoomPointerDown = (event) => {
    if (event.pointerType === 'touch' || this.#scale <= 1 || !this.#isImageTarget(event.target)) return;
    if (event.button !== undefined && event.button !== 0) return;
    this.#drag = { id: event.pointerId, startX: event.clientX, startY: event.clientY, x: this.#pan.x, y: this.#pan.y, target: event.target };
    // Keep click/double-click targeted at the photo rather than its scroller.
    event.target.setPointerCapture(event.pointerId);
  };

  handleZoomPointerMove = (event) => {
    if (!this.#drag || this.#drag.id !== event.pointerId) return;
    this.#setZoom(this.#scale, this.#drag.x + event.clientX - this.#drag.startX, this.#drag.y + event.clientY - this.#drag.startY);
  };

  handleZoomPointerUp = (event) => {
    if (this.#drag?.id !== event.pointerId) return;
    this.#releaseMouseCapture();
    this.#drag = null;
  };

  #releaseMouseCapture() {
    const drag = this.#drag;
    if (drag?.target?.hasPointerCapture(drag.id)) drag.target.releasePointerCapture(drag.id);
  }

  handleZoomDragStart = (event) => {
    if (this.#isImageTarget(event.target)) event.preventDefault();
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (this.classList.contains('bs-fullscreen-gallery')) return this.closeDialog();
    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#resetZoom();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select, video, audio, [contenteditable="true"]')) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    const actions = {
      ArrowLeft: () => rtl ? this.next() : this.previous(),
      ArrowRight: () => rtl ? this.previous() : this.next(),
      Home: () => this.selectThumbnail(0),
      End: () => this.selectThumbnail(this.refs.media.length - 1),
    };
    if (actions[event.key]) {
      event.preventDefault();
      actions[event.key]();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    index = Number(index);
    if (!Number.isInteger(index) || index < 0 || index >= this.refs.media.length) return;
    const behavior = prefersReducedMotion() ? 'instant' : options.behavior;
    scrollIntoView(this.refs.media[index], {
      ancestor: this.#scrollTarget,
      behavior,
      block: 'start',
      inline: 'start',
    });
    this.#updateSelection(index);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}


